import { useLanguage } from '../../contexts/LanguageContext';
import { cn } from '../../lib/utils';
import RunningWorkoutViz from './RunningWorkoutViz';

function formatDurationSec(sec) {
  if (sec == null || !Number.isFinite(Number(sec)) || Number(sec) <= 0) return '—';
  const s = Math.round(Number(sec));
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${m}:${String(r).padStart(2, '0')}`;
}

function formatDistanceM(m) {
  if (m == null || !Number.isFinite(Number(m)) || Number(m) <= 0) return '—';
  const km = Number(m) / 1000;
  if (km >= 1) return `${km.toFixed(2)} km`;
  return `${Math.round(Number(m))} m`;
}

function splitUsesDistancePrimary(s) {
  return String(s.split_measure_mode || '').toLowerCase() === 'distance';
}

function primaryMeasure(s) {
  if (splitUsesDistancePrimary(s)) {
    return formatDistanceM(s.distance_m);
  }
  return formatDurationSec(s.duration_sec);
}

function formatAvgWatts(w) {
  if (w == null || !Number.isFinite(Number(w))) return '—';
  const n = Number(w);
  return n % 1 === 0 ? String(Math.round(n)) : n.toFixed(1);
}

function intensityLabel(ic, t) {
  const c = String(ic || 'work').toLowerCase();
  if (c === 'warmup') return t('activityDetail.draftIntensityWarmup');
  if (c === 'cooldown') return t('activityDetail.draftIntensityCooldown');
  if (c === 'rest') return t('activityDetail.draftIntensityRest');
  return t('activityDetail.draftIntensityWork');
}

/**
 * Read-only workout preview for activity → save-as-exercise draft (viz + table).
 */
export default function RunningExerciseDraftPreview({ splits, className, vizHeightPx = 48 }) {
  const { t } = useLanguage();
  const list = Array.isArray(splits) ? splits : [];

  return (
    <div className={cn('space-y-3', className)}>
      {list.length > 0 && (
        <RunningWorkoutViz splits={list} heightPx={vizHeightPx} className="w-full" />
      )}
      {list.length > 0 && (
        <div className="rounded-lg border border-border overflow-hidden">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-border bg-muted/40 text-muted-foreground">
                <th className="text-left p-2 font-medium">{t('runningExercise.colSplit')}</th>
                <th className="text-left p-2 font-medium">{t('runningExercise.colSegment')}</th>
                <th className="text-left p-2 font-medium">{t('runningExercise.segClass')}</th>
                <th className="text-right p-2 font-medium">{t('activityDetail.draftColWatts')}</th>
                <th className="text-right p-2 font-medium">{t('activityDetail.draftColMeasure')}</th>
              </tr>
            </thead>
            <tbody>
              {list.map((s, i) => (
                <tr key={i} className="border-b border-border/80 last:border-0">
                  <td className="p-2 tabular-nums text-muted-foreground">{i + 1}</td>
                  <td className="p-2 text-foreground">{s.label || '—'}</td>
                  <td className="p-2 text-foreground">{intensityLabel(s.run_intensity_class, t)}</td>
                  <td className="p-2 text-right tabular-nums text-foreground">{formatAvgWatts(s.average_watts)}</td>
                  <td className="p-2 text-right tabular-nums text-foreground">{primaryMeasure(s)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
